/**
 * As consultas de caso.
 *
 * `GET /casos?page=&pageSize=` é a listagem, com o nome do paciente MASCARADO (`NR-114`). `GET /casos/{id}` é o detalhe,
 * com o nome completo — e esse acesso entra na trilha. Os dois estão no `contracts/openapi/anery-api.v1.json`, e o gate
 * de drift garante que não mentem. Não há `/casos/{id}/eventos`: era imaginário e a linha do tempo saiu da tela por isso.
 *
 * O corpo do 200 é ARRAY PURO. Sem envelope, sem `total` — e por isso a tela
 * não mostra contagem total nem "página X de Y": esse número não existe do
 * lado de lá, e estimá-lo seria inventar de novo.
 */

import { queryOptions } from '@tanstack/react-query';
import type {
  CaseDetail,
  CaseExtractedFieldsResponse,
  CaseThreadResponse,
  CaseTimelineResponse,
  ListaDeCasos,
  ParametrosDeListagemDeCasos,
} from '@anery/api-contracts';

import { requisitar } from '../../../shared/api/clienteHttp';
import { etapaDoEstado } from '../etapas';
import { ErroDaApi } from '../../../shared/api/erros';
import {
  mapearCamposExtraidosDoCaso,
  mapearCasoDetalhado,
  mapearFilaDeCasos,
  mapearLinhaDoTempo,
  mapearThreadDoCaso,
} from './mapeadores';
import type { CampoExtraidoDoCaso, CasoDaFila, CasoDetalhado, EventoDaLinhaDoTempo, MensagemDaThread } from './tipos';

/**
 * Casos por página. Cabe no teto de 100 da API (`GetCasesQuery.MaxPageSize`)
 * e dá uma fila inteira de operação numa tela só.
 */
export const TAMANHO_DE_PAGINA = 50;

/**
 * Só o que a listagem PAGINADA usa. `Required<ParametrosDeListagemDeCasos>`
 * passou a exigir também `recorte`/`nucleo`/`operadora`/`diasDePermanencia`,
 * que só valem no recorte da captação.
 */
export type ParametrosDaPagina = Required<Pick<ParametrosDeListagemDeCasos, 'page' | 'pageSize'>>;

export const chavesDeCasos = {
  raiz: ['casos'] as const,
  lista: (parametros: ParametrosDaPagina) => ['casos', 'lista', parametros] as const,
  captacao: (filtros: FiltrosDaCaptacao) => ['casos', 'captacao', filtros] as const,
  detalhe: (id: string) => ['casos', id, 'detalhe'] as const,
  thread: (id: string) => ['casos', id, 'thread'] as const,
  camposExtraidos: (id: string) => ['casos', id, 'campos-extraidos'] as const,
  linhaDoTempo: (id: string) => ['casos', id, 'linha-do-tempo'] as const,
  candidatosDoIw: (id: string) => ['casos', id, 'candidatos-do-iw'] as const,
  enviosAoIw: (id: string) => ['casos', id, 'envios-ao-iw'] as const,
  admissoesDoPacienteNoIw: (id: string) => ['casos', id, 'admissoes-do-paciente-no-iw'] as const,
  cadastroPrevisto: (id: string) => ['casos', id, 'cadastro-previsto'] as const,
};

function montarConsulta(parametros: ParametrosDaPagina): string {
  const busca = new URLSearchParams({
    page: String(parametros.page),
    pageSize: String(parametros.pageSize),
  });
  return `?${busca.toString()}`;
}

function buscarFila(
  parametros: ParametrosDaPagina,
  sinal: AbortSignal,
): Promise<readonly CasoDaFila[]> {
  // O mapeamento acontece AQUI, colado no fetch: nenhum componente chega a
  // ver `CaseListItem`, e um corpo fora do contrato vira erro na fronteira
  // em vez de célula vazia lá na frente.
  return requisitar<ListaDeCasos>(`/casos${montarConsulta(parametros)}`, { sinal }).then(mapearFilaDeCasos);
}

/** Uma página da fila. `pagina` é 1-based, como a API. */
export function opcoesDaFilaDeCasos(pagina: number) {
  const parametros = { page: pagina, pageSize: TAMANHO_DE_PAGINA };
  return queryOptions({
    queryKey: chavesDeCasos.lista(parametros),
    queryFn: ({ signal }) => buscarFila(parametros, signal),
  });
}

/** `404` é a resposta para id inexistente E para caso de outro núcleo — a API não os distingue. */
function ehCasoNaoEncontrado(erro: unknown): boolean {
  return erro instanceof ErroDaApi && erro.origem === 'http' && erro.status === 404;
}

function buscarCaso(id: string, sinal: AbortSignal): Promise<CasoDetalhado | null> {
  return requisitar<CaseDetail>(`/casos/${encodeURIComponent(id)}`, { sinal }).then(mapearCasoDetalhado, (erro: unknown) => {
    // Ausente é ESTADO com nome, não erro de rede: a API não diz se o caso nunca existiu ou é de outro núcleo.
    if (ehCasoNaoEncontrado(erro)) return null;
    throw erro;
  });
}

/**
 * Um caso (`GET /casos/{id}`) com o nome COMPLETO do paciente e os gatilhos que ainda valem; `null` quando a API
 * responde 404.
 */
export function opcoesDoCaso(id: string) {
  return queryOptions({
    queryKey: chavesDeCasos.detalhe(id),
    queryFn: ({ signal }) => buscarCaso(id, signal),
    enabled: id !== '',
    ...AO_VIVO,
  });
}

function buscarThread(id: string, sinal: AbortSignal): Promise<readonly MensagemDaThread[]> {
  return requisitar<CaseThreadResponse>(`/casos/${id}/thread`, { sinal }).then(mapearThreadDoCaso);
}

/**
 * A thread de e-mail de um caso (`GET /casos/{id}/thread`, `TP-023`, `T6`/`T7`).
 * Exige sessão de pessoa (cookie) — o mesmo esquema que `/triagem` já exige;
 * `requisitar` já manda `credentials: 'same-origin'` em toda chamada.
 */
export function opcoesDaThreadDoCaso(id: string) {
  return queryOptions({
    queryKey: chavesDeCasos.thread(id),
    queryFn: ({ signal }) => buscarThread(id, signal),
    enabled: id !== '',
  });
}

function buscarCamposExtraidos(id: string, sinal: AbortSignal): Promise<readonly CampoExtraidoDoCaso[]> {
  return requisitar<CaseExtractedFieldsResponse>(`/casos/${id}/campos-extraidos`, { sinal }).then(
    mapearCamposExtraidosDoCaso,
  );
}

/**
 * Os campos lidos dos e-mails e anexos de um caso
 * (`GET /casos/{id}/campos-extraidos`, `TP-031`, `T6`/`T7`). Exige sessão de
 * pessoa (cookie), mesmo esquema de `opcoesDaThreadDoCaso`.
 */
export function opcoesDosCamposExtraidosDoCaso(id: string) {
  return queryOptions({
    queryKey: chavesDeCasos.camposExtraidos(id),
    queryFn: ({ signal }) => buscarCamposExtraidos(id, signal),
    enabled: id !== '',
  });
}

function buscarLinhaDoTempo(id: string, sinal: AbortSignal): Promise<readonly EventoDaLinhaDoTempo[]> {
  return requisitar<CaseTimelineResponse>(`/casos/${id}/linha-do-tempo`, { sinal }).then(mapearLinhaDoTempo);
}

/**
 * A linha do tempo do Caso (`GET /casos/{id}/linha-do-tempo`, `TP-048`, `T6`), do mais antigo ao mais recente. Exige
 * sessão de pessoa (cookie), mesmo esquema de `opcoesDaThreadDoCaso`.
 */
export function opcoesDaLinhaDoTempoDoCaso(id: string) {
  return queryOptions({
    queryKey: chavesDeCasos.linhaDoTempo(id),
    queryFn: ({ signal }) => buscarLinhaDoTempo(id, signal),
    enabled: id !== '',
    ...AO_VIVO,
  });
}


// ---------------------------------------------------------------------------
// O recorte da CAPTAÇÃO — a mesma rota, o mesmo DTO, o mesmo mapeador.
//
// `?recorte=captacao` troca a página pelo conjunto fechado: `encerrado` fora, e
// `autorizado`/`recusado` só dentro da janela de permanência. O recorte é do
// SERVIDOR, não desta camada: refazê-lo aqui seria uma segunda verdade, e a
// trilha de acesso já registrou exatamente o que veio.
// ---------------------------------------------------------------------------

export interface FiltrosDaCaptacao {
  /** `null` = todos os núcleos que o escopo da sessão permite. */
  readonly nucleo: string | null;
  readonly operadora: string | null;
}

export const SEM_FILTRO: FiltrosDaCaptacao = { nucleo: null, operadora: null };

/**
 * Entre uma leitura e a seguinte (`S10`).
 *
 * Trinta segundos porque o quadro acompanha trabalho humano: um Caso muda de
 * etapa quando alguém clica, não em rajada. Mais curto castigaria o banco para
 * mostrar a mesma tela; mais longo faria a pessoa duvidar e recarregar a
 * página, que é justamente o gesto que a atualização automática evita.
 */
export const INTERVALO_DE_ATUALIZACAO_MS = 30_000;

/**
 * O caso aberto se relê ao voltar para a aba: o que outra pessoa fez enquanto ela estava em outra janela aparece sem
 * recarregar. Sem relógio: o que a própria pessoa faz já relê o Caso (`invalidarOQueAProvaMuda`), e cada leitura entra
 * na trilha de acesso.
 */
export const AO_VIVO = { refetchOnWindowFocus: true } as const;

function consultaDaCaptacao(filtros: FiltrosDaCaptacao): string {
  const busca = new URLSearchParams({ recorte: 'captacao' });
  if (filtros.nucleo !== null) busca.set('nucleo', filtros.nucleo);
  if (filtros.operadora !== null) busca.set('operadora', filtros.operadora);
  return `?${busca.toString()}`;
}

/**
 * `F4` manda o log registrar QUAL substatus não foi reconhecido.
 *
 * Aqui e não no componente: é uma vez por leitura (30s), e não uma por render.
 * E é o lugar honesto — o que chegou pelo fio e não entendemos.
 */
function avisarSubstatusDesconhecido(casos: readonly CasoDaFila[]): void {
  const desconhecidos = [
    ...new Set(casos.filter((caso) => etapaDoEstado(caso.estado) === 'desconhecida').map((caso) => caso.estado)),
  ];

  if (desconhecidos.length > 0) {
    // Só o token do substatus. Nada de id nem de nome: é log de navegador.
    console.warn('casos: substatus fora do mapeamento vigente', desconhecidos);
  }
}

export function opcoesDaCaptacao(filtros: FiltrosDaCaptacao = SEM_FILTRO) {
  return queryOptions({
    queryKey: chavesDeCasos.captacao(filtros),
    queryFn: ({ signal: sinal }): Promise<readonly CasoDaFila[]> =>
      requisitar<ListaDeCasos>(`/casos${consultaDaCaptacao(filtros)}`, { sinal })
        .then(mapearFilaDeCasos)
        .then((casos) => {
          avisarSubstatusDesconhecido(casos);
          return casos;
        }),
    refetchInterval: INTERVALO_DE_ATUALIZACAO_MS,
    // Aba em segundo plano NÃO lê. Cada leitura grava na trilha de acesso
    // (`registro_acesso`), e uma aba esquecida aberta a noite inteira encheria
    // a trilha LGPD de leituras que ninguém fez.
    refetchIntervalInBackground: false,
  });
}
